import { mergeTopicContent, TOPIC_ENTRY_KIND_BLOCK, TOPIC_ENTRY_KIND_CONTAINER } from '../../courses/topicContent';
import { TOPIC_BLOCK_EDITOR_REGISTRY } from '../../courses/topicBlockEditors';
import { getTopicBlockType } from '../../courses/topicBlockTypes';
import { BlockTypeBadge } from '../../courses/preview/blockTypeIcons';
import BlurredPreviewWrapper from '../../courses/preview/BlurredPreviewWrapper';
import { mergeBlocks, BLOCK_KIND_CONTAINER, BLOCK_KIND_INTERACTIVE } from '../../exercise-templates/blockList';
import { getBlockType } from '../../../variables/exerciseBlockTypes';
import BLOCK_EDITOR_REGISTRY from '../../exercise-templates/block-editors/registry';
import ContainerLayout from '../../../components/ContainerLayout';
import { safeParseContainerConfig } from '../../../variables/containerLayoutTypes';
import GroupExerciseAnswerState from './GroupExerciseAnswerState';
import GroupConversationBlockEntry from './GroupConversationBlockEntry';
import NotePin from '../../students/workspace/NotePin';
import { useNavigate } from 'react-router-dom';
import IconCanvasOpen from '../../../components/icons/IconCanvasOpen';
import '../../../css/topic-canvas.css';

// Group-wide mirror of students/workspace/WorkspaceTopicSection.jsx. Same
// read-only-review spirit, but split along the same line the whole
// architecture is split along: plain Topic content (text/media) and static
// exercise blocks have no per-student state at all, so they render exactly
// once, same as any teacher-authoring preview. Interactive exercise blocks
// (the only genuinely answerable kind) render through
// GroupExerciseAnswerState instead of a single ExerciseAnswerState - one
// row per member who actually answered, nobody merged into anybody else's
// row. embed_conversation is dispatched to GroupConversationBlockEntry
// instead (own per-member step-history window, same split single-student's
// own ExerciseBlockEntry already makes) - was missing entirely until a real
// bug report ("results never update, live or after reload") traced back to
// this: conversation progress was already being fetched into
// activityByStudentId[..].progressByElementId, just never read by anything
// in this file. Block markers are still not ported here (backend only
// propagates notes+canvas so far).
function TopicBlockEntry({ block, notes, workspaceId, onNotesChanged }) {
   const registryEntry = TOPIC_BLOCK_EDITOR_REGISTRY[block.type];
   if (!registryEntry) return null;
   const blockType = getTopicBlockType(block.type) || {};
   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <BlurredPreviewWrapper elementType='TOPIC' elementId={block.id} blurEnabled={!!block.blurEnabled}>
            <div className='course-preview-block-content'>
               <div className='workspace-frozen-content'>{registryEntry.renderPreview(block)}</div>
            </div>
         </BlurredPreviewWrapper>
         <NotePin notes={notes} kind='topicElement' targetId={block.id} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
      </div>
   );
}

function TopicContainerGroup({ container, elements, notesByTarget, workspaceId, onNotesChanged }) {
   const children = mergeTopicContent({ elements, containers: [], containerId: container.id });
   if (children.length === 0) return null;
   const config = safeParseContainerConfig(container.config, container.layout);
   return (
      <ContainerLayout layout={container.layout} config={config}>
         {children.map(entry => (
            <div className='course-preview-container-item' key={entry.item.id}>
               <TopicBlockEntry block={entry.item} notes={notesByTarget['topicElement:' + entry.item.id] || []}
                  workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
            </div>
         ))}
      </ContainerLayout>
   );
}

function renderExerciseBlockContent(block) {
   const registryEntry = BLOCK_EDITOR_REGISTRY[block.type];
   return registryEntry ? registryEntry.renderPreview(block) : null;
}

function ExerciseBlockEntry({ block, notesByTarget, members, activityByStudentId, workspaceId, onNotesChanged }) {
   if (block.type === 'embed_conversation') {
      return <GroupConversationBlockEntry block={block} notesByTarget={notesByTarget} members={members}
         activityByStudentId={activityByStudentId} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />;
   }
   const blockType = getBlockType(block.type) || {};
   const content = renderExerciseBlockContent(block);
   if (!content) return null;

   const savedStateByStudentId = {};
   members.forEach(m => {
      const slice = activityByStudentId[m.studentId];
      if (slice && slice.savedStateByElementId[block.id]) savedStateByStudentId[m.studentId] = slice.savedStateByElementId[block.id];
   });

   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <BlurredPreviewWrapper elementType='EXERCISE_INTERACTIVE' elementId={block.id} blurEnabled={!!block.blurEnabled}>
            <div className='course-preview-block-content'>
               <div className='workspace-frozen-content'>{content}</div>
               <GroupExerciseAnswerState block={block} members={members} savedStateByStudentId={savedStateByStudentId} />
            </div>
         </BlurredPreviewWrapper>
         <NotePin notes={notesByTarget['exerciseInteractiveElement:' + block.id] || []}
            kind='exerciseInteractiveElement' targetId={block.id} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
      </div>
   );
}

function ExerciseStaticBlockEntry({ block, notesByTarget, workspaceId, onNotesChanged }) {
   const blockType = getBlockType(block.type) || {};
   const registryEntry = BLOCK_EDITOR_REGISTRY[block.type];
   const content = registryEntry ? registryEntry.renderPreview(block) : null;
   if (!content) return null;
   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <BlurredPreviewWrapper elementType='EXERCISE' elementId={block.id} blurEnabled={!!block.blurEnabled}>
            <div className='course-preview-block-content'>
               <div className='workspace-frozen-content'>{content}</div>
            </div>
         </BlurredPreviewWrapper>
         <NotePin notes={notesByTarget['exerciseElement:' + block.id] || []}
            kind='exerciseElement' targetId={block.id} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
      </div>
   );
}

function ExerciseContainerGroup({ container, elements, interactiveElements, notesByTarget, members, activityByStudentId, workspaceId, onNotesChanged }) {
   const children = mergeBlocks(elements, interactiveElements, [], container.id);
   if (children.length === 0) return null;
   const config = safeParseContainerConfig(container.config, container.layout);
   return (
      <ContainerLayout layout={container.layout} config={config}>
         {children.map(entry => (
            <div className='course-preview-container-item' key={entry.kind + '-' + entry.block.id}>
               {entry.kind === BLOCK_KIND_INTERACTIVE ? (
                  <ExerciseBlockEntry block={entry.block} notesByTarget={notesByTarget} members={members}
                     activityByStudentId={activityByStudentId} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
               ) : (
                  <ExerciseStaticBlockEntry block={entry.block} notesByTarget={notesByTarget} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
               )}
            </div>
         ))}
      </ContainerLayout>
   );
}

function ExerciseLinkSection({ exercise, notesByTarget, members, activityByStudentId, workspaceId, onNotesChanged }) {
   const merged = mergeBlocks(exercise.elements, exercise.interactiveElements, exercise.containers);
   if (!exercise.link.exerciseTemplate) return null;
   return (
      <div className='course-preview-exercise'>
         <h2 className='course-preview-exercise-title'>{exercise.link.exerciseTemplate.name}</h2>
         {merged.length === 0 ? (
            <p className='course-preview-empty-hint'>Ця вправа ще порожня.</p>
         ) : (
            <div className='course-preview-timeline'>
               {merged.map(entry => (
                  entry.kind === BLOCK_KIND_CONTAINER
                     ? <ExerciseContainerGroup key={'container-' + entry.block.id} container={entry.block}
                        elements={exercise.elements} interactiveElements={exercise.interactiveElements}
                        notesByTarget={notesByTarget} members={members} activityByStudentId={activityByStudentId}
                        workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
                     : entry.kind === BLOCK_KIND_INTERACTIVE
                     ? <ExerciseBlockEntry key={'el-' + entry.block.id} block={entry.block}
                        notesByTarget={notesByTarget} members={members} activityByStudentId={activityByStudentId}
                        workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
                     : <ExerciseStaticBlockEntry key={'el-' + entry.block.id} block={entry.block}
                        notesByTarget={notesByTarget} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
               ))}
            </div>
         )}
      </div>
   );
}

export default function GroupWorkspaceTopicSection({ topicEntry, notesByTarget, members, activityByStudentId, workspaceId, groupId, courseId, onNotesChanged, sectionRef }) {
   const { topic, elements, containers, exerciseLinks } = topicEntry;
   const topLevel = mergeTopicContent({ elements, containers, exerciseLinks: exerciseLinks.map(e => e.link), containerId: null });
   const navigate = useNavigate();

   // Canvas is fully synced across the group (backend-propagated) - opening
   // it through ANY one member's own per-student canvas route shows the
   // exact same shared content, so this reuses that existing route
   // (students/:id/workspace/:courseId/canvas/:topicId) pointed at whichever
   // member the workspace resolver returned as representative, rather than
   // building a second canvas page.
   const representativeStudentId = members.length > 0 ? members[0].studentId : null;
   const openCanvas = () => {
      if (representativeStudentId) navigate('/students/' + representativeStudentId + '/workspace/' + courseId + '/canvas/' + topic.id);
   };

   return (
      <div className='course-preview-topic-section workspace-topic-canvas-anchor' ref={sectionRef} data-topic-id={topic.id}>
         {representativeStudentId ? (
            <span className='workspace-topic-canvas-trigger' title='Відкрити спільний канвас групи'
               onClick={openCanvas}>
               <IconCanvasOpen style={{ width: 22, height: 22 }} />
            </span>
         ) : ''}
         <div className='course-preview-header'>
            <div className='course-preview-eyebrow-row'>
               <span className='course-preview-eyebrow'>Тема</span>
            </div>
            <h1 className='course-preview-title'>{topic.name}</h1>
            {topic.description ? <p className='course-preview-description'>{topic.description}</p> : ''}
            <div className='course-preview-header-rule' />
         </div>

         {topLevel.length > 0 ? (
            <div className='course-preview-timeline'>
               {topLevel.map(entry => {
                  if (entry.kind === TOPIC_ENTRY_KIND_BLOCK) {
                     return (
                        <TopicBlockEntry key={'el-' + entry.item.id} block={entry.item}
                           notes={notesByTarget['topicElement:' + entry.item.id] || []}
                           workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
                     );
                  }
                  if (entry.kind === TOPIC_ENTRY_KIND_CONTAINER) {
                     return (
                        <TopicContainerGroup key={'container-' + entry.item.id} container={entry.item} elements={elements}
                           notesByTarget={notesByTarget} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
                     );
                  }
                  const exercise = exerciseLinks.find(e => e.link.id === entry.item.id);
                  return exercise ? (
                     <ExerciseLinkSection key={'ex-' + entry.item.id} exercise={exercise}
                        notesByTarget={notesByTarget} members={members} activityByStudentId={activityByStudentId}
                        workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
                  ) : null;
               })}
            </div>
         ) : (
            <p className='course-preview-empty-hint'>У цій темі ще немає контенту.</p>
         )}
      </div>
   );
}
